import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FACTOR, parseDecimal, PERCENT, PRICE, QUANTITY, type DecimalSpec, type ItemDto, type ItemFormOptions, type ItemInput, type ItemListDto } from "@delta/shared";
import { useLang } from "../../i18n";
import { api, apiUpload, ApiFailure } from "../../lib/api";
import { errorText, fieldText } from "../../lib/errors";
import { Icon } from "../../components/Icon";
import { PageHead, Shell } from "../../components/Shell";
import { Field } from "../../components/Modal";
import { useToast } from "../../components/Toast";
import { BARCODE_RE, dec, pct, qty, sampleEan13 } from "./common";

// Items › Create item and Edit item, ported from the prototype (erp-items.js renderItemEditor) and the audited form
// (/items/create: itemCategoryId, code, titleAr/En, descriptionAr/En, discount, minQty, units[i] with unitId,
// pieces, the three prices and barcodes[j], itemKindId, taxTypeId, priceIncludeTax, quickList, image).
// Item kind and tax type are required and chosen from the organization's lists; a new item starts with the first.
// Numbers stay text from the input to the database, so nothing is rounded on the way.

interface UnitRow { id?: string; key: number; unitId: string; pieces: string; priceWholesale: string; priceHalf: string; priceRetail: string; barcodes: string[]; typed: string }
interface Draft { categoryId: string; itemKindId: string; taxTypeId: string; code: string; nameAr: string; nameEn: string; descriptionAr: string; descriptionEn: string; discount: string; minQty: string; priceIncludeTax: boolean; quickList: boolean; tobaccoFee: boolean; units: UnitRow[] }
const IMAGE_TYPES = ["image/png", "image/jpeg", "image/webp"], IMAGE_MAX = 512 * 1024;
let seq = 0;

function fromItem(it: ItemDto): Draft {
  return {
    categoryId: it.categoryId, itemKindId: it.itemKindId ?? "", taxTypeId: it.taxTypeId ?? "", code: it.code, nameAr: it.nameAr, nameEn: it.nameEn, descriptionAr: it.descriptionAr, descriptionEn: it.descriptionEn,
    discount: dec(it.discount, 0), minQty: qty(it.minQty), priceIncludeTax: it.priceIncludeTax, quickList: it.quickList, tobaccoFee: it.tobaccoFee,
    units: it.units.map((u) => ({ id: u.id, key: ++seq, unitId: u.unitId, pieces: qty(u.pieces), priceWholesale: dec(u.priceWholesale), priceHalf: dec(u.priceHalf), priceRetail: dec(u.priceRetail), barcodes: [...u.barcodes], typed: "" }))
  };
}
const blankUnit = (unitId: string, pieces: string): UnitRow => ({ key: ++seq, unitId, pieces, priceWholesale: "", priceHalf: "", priceRetail: "", barcodes: [], typed: "" });

export function ItemEditor() {
  const { id } = useParams();
  const { t, L } = useLang();
  const toast = useToast();
  const nav = useNavigate();
  const [opts, setOpts] = useState<ItemFormOptions | null>(null);
  const [src, setSrc] = useState<ItemDto | null>(null);
  const [v, setV] = useState<Draft | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [errs, setErrs] = useState<Record<string, string>>({});
  const [alert, setAlert] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [image, setImage] = useState<{ file: File | null; url: string | null } | null>(null); // null: unchanged
  // A new item is saved before its image is uploaded. If the upload then fails, the saved item is kept here, so
  // the image can be retried on that item instead of creating it again (which would clash on its code).
  const [saved, setSaved] = useState<ItemDto | null>(null);
  const [imageFailed, setImageFailed] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const o = await api<ItemFormOptions>("GET", "/items/form-options");
        setOpts(o);
        if (id) { const it = await api<ItemDto>("GET", `/items/${id}`); setSrc(it); setV(fromItem(it)); }
        else setV({ categoryId: "", itemKindId: o.itemKinds[0]?.id ?? "", taxTypeId: o.taxTypes[0]?.id ?? "", code: "", nameAr: "", nameEn: "", descriptionAr: "", descriptionEn: "", discount: "0", minQty: "0", priceIncludeTax: false, quickList: false, tobaccoFee: false, units: [blankUnit(o.units[0]?.id ?? "", "1")] });
      } catch (e) { setFailed(e instanceof ApiFailure && e.status === 404 ? t("itemNotFound") : errorText(t, e)); }
    })();
  }, [id, t]);
  useEffect(() => () => { if (image?.url) URL.revokeObjectURL(image.url); }, [image]);

  const set = <K extends keyof Draft>(k: K, x: Draft[K]) => { setV((o) => o && { ...o, [k]: x }); setErrs((e) => ({ ...e, [k]: "" })); setAlert(null); };
  const setUnit = (i: number, patch: Partial<UnitRow>) => {
    setV((o) => o && { ...o, units: o.units.map((u, j) => (j === i ? { ...u, ...patch } : u)) });
    setErrs((e) => Object.fromEntries(Object.entries(e).filter(([k]) => !k.startsWith(`units.${i}.`))));
    setAlert(null);
  };
  const barcodeCount = useMemo(() => v?.units.reduce((a, u) => a + u.barcodes.length, 0) ?? 0, [v]);

  /** Is this barcode free? Checked against this form and, on the server, against every other item. */
  async function barcodeFree(code: string): Promise<boolean> {
    if (v!.units.some((u) => u.barcodes.includes(code))) return false;
    try { const hit = await api<{ itemId: string }>("GET", `/items/barcode/${encodeURIComponent(code)}`); return hit.itemId === src?.id; } // this item's own saved barcode, removed and typed again
    catch (e) { if (e instanceof ApiFailure && e.status === 404) return true; throw e; }
  }
  async function addBarcode(i: number, code: string) {
    code = code.trim();
    if (!code) return;
    if (!BARCODE_RE.test(code)) { toast(t("barcodeFormat")); return; }
    try {
      if (!(await barcodeFree(code))) { toast(t("barcodeTaken", { x: code })); return; }
      setUnit(i, { barcodes: [...v!.units[i]!.barcodes, code], typed: "" });
    } catch (e) { toast(errorText(t, e)); }
  }
  async function generate(i: number) {
    try {
      for (let n = 0; n < 5; n++) { const code = sampleEan13(); if (await barcodeFree(code)) { setUnit(i, { barcodes: [...v!.units[i]!.barcodes, code] }); return; } }
    } catch (e) { toast(errorText(t, e)); }
  }
  async function autoCode() {
    const cat = opts?.categories.find((c) => c.id === v!.categoryId);
    const pre = (cat?.nameEn.replace(/[^A-Za-z]/g, "").slice(0, 3).toUpperCase() || "ITM");
    try {
      const taken = new Set((await api<ItemListDto[]>("GET", `/items?code=${encodeURIComponent(pre + "-")}`)).map((x) => x.code));
      let n = 100;
      while (taken.has(`${pre}-${n}`)) n++;
      set("code", `${pre}-${n}`);
    } catch (e) { toast(errorText(t, e)); }
  }
  function chooseImage(file?: File) {
    if (!file) return;
    if (!IMAGE_TYPES.includes(file.type)) { toast(t("logoType")); return; }
    if (file.size > IMAGE_MAX) { toast(t("logoTooLarge")); return; }
    setImage({ file, url: URL.createObjectURL(file) });
  }

  // Client checks mirror the server's (shared/src/amount.ts); the server checks again and has the last word.
  function check(d: Draft): Record<string, string> {
    const e: Record<string, string> = {};
    const num = (k: string, x: string, spec: DecimalSpec, optional = false) => {
      if (optional && !x.trim()) return;
      const r = parseDecimal(x, spec);
      if ("error" in r) e[k] = k === "discount" && r.error === "out_of_range" ? t("percentRange") : fieldText(t, r.error);
    };
    if (!d.categoryId) e.categoryId = t("requiredField");
    // A migrated item without a code may stay without one (D-14, provisional); Delta never generates one.
    if (!d.code.trim() && !src?.legacyFlags.includes("no_code")) e.code = t("requiredField");
    if (!d.nameAr.trim()) e.nameAr = t("requiredField");
    if (!d.nameEn.trim()) e.nameEn = t("requiredField");
    if (!d.itemKindId) e.itemKindId = t("requiredField");
    if (!d.taxTypeId) e.taxTypeId = t("requiredField");
    num("discount", d.discount, PERCENT); num("minQty", d.minQty, QUANTITY);
    const seen = new Set<string>();
    d.units.forEach((u, i) => {
      num(`units.${i}.pieces`, u.pieces, FACTOR);
      num(`units.${i}.priceRetail`, u.priceRetail, PRICE);
      num(`units.${i}.priceWholesale`, u.priceWholesale, PRICE, true);
      num(`units.${i}.priceHalf`, u.priceHalf, PRICE, true);
      const p = parseDecimal(u.pieces, FACTOR);
      if (i === 0 && "value" in p && p.value !== "1.0000") e[`units.0.pieces`] = t("baseUnitOne");
      const key = `${u.unitId}|${"value" in p ? p.value : u.pieces}`;
      if (seen.has(key)) e[`units.${i}.unitId`] = t("unitDuplicate");
      seen.add(key);
    });
    return e;
  }

  function finish(it: ItemDto) {
    toast(t(src ? "updatedOk" : "addedOk", { x: L(it) }));
    nav(`/items/show/${it.id}`);
  }
  /** Uploads or removes the image of a saved item. False, with the reason shown, when that step fails. */
  async function applyImage(it: ItemDto): Promise<boolean> {
    try {
      if (image?.file) await apiUpload(`/items/${it.id}/image`, image.file);
      else if (image && it.image) await api("DELETE", `/items/${it.id}/image`);
      setImageFailed(null);
      return true;
    } catch (err) { setImageFailed(errorText(t, err)); return false; }
  }
  async function retryImage() {
    setBusy(true);
    const it = (saved ?? src)!;
    try { if (await applyImage(it)) finish(it); } finally { setBusy(false); }
  }

  async function save() {
    if (saved) return retryImage(); // the item is already stored: only the image is left to do
    const d = v!;
    const e = check(d);
    setErrs(e);
    if (Object.keys(e).length) {
      setAlert(Object.values(e).includes(t("unitDuplicate")) ? t("unitDuplicate") : t("fixRequired"));
      return;
    }
    const body: ItemInput = {
      categoryId: d.categoryId, itemKindId: d.itemKindId, taxTypeId: d.taxTypeId, code: d.code.trim(), nameAr: d.nameAr, nameEn: d.nameEn, descriptionAr: d.descriptionAr, descriptionEn: d.descriptionEn,
      discount: d.discount.trim(), minQty: d.minQty.trim(), priceIncludeTax: d.priceIncludeTax, quickList: d.quickList, tobaccoFee: d.tobaccoFee,
      units: d.units.map((u) => ({ ...(u.id ? { id: u.id } : {}), unitId: u.unitId, pieces: u.pieces.trim(), priceWholesale: u.priceWholesale.trim(), priceHalf: u.priceHalf.trim(), priceRetail: u.priceRetail.trim(), barcodes: u.barcodes }))
    };
    setBusy(true); setAlert(null);
    let it: ItemDto;
    try { it = src ? await api<ItemDto>("PUT", `/items/${src.id}`, body) : await api<ItemDto>("POST", "/items", body); }
    catch (err) {
      const f = err instanceof ApiFailure ? err.fields : {};
      setErrs(Object.fromEntries(Object.entries(f).map(([k, c]) => [k, k === "discount" && c === "out_of_range" ? t("percentRange") : fieldText(t, c)])));
      setAlert(errorText(t, err));
      setBusy(false);
      return;
    }
    if (!src) setSaved(it);
    if (await applyImage(it)) finish(it);
    else if (src) setSrc(it);
    setBusy(false);
  }

  const title = src ? `${t("editItem")} · ${L(src)}` : t("createItem");
  const crumbs: [string, string?][] = [[t("home"), "/home"], [t("items")], [t("allItems"), "/items/allItems"], [src ? t("editItem") : t("createItem")]];
  if (failed || !v || !opts) return (
    <Shell navKey="/items/allItems"><PageHead crumbs={crumbs} title={title} />
      <section className="card"><div className={`state-box ${failed ? "error" : ""}`}>{failed ? <><Icon name="alert" /><h3>{t("loadErrorTitle")}</h3><p>{failed}</p></> : <p>{t("loading")}</p>}</div></section>
    </Shell>
  );

  const inp = (k: "code" | "nameAr" | "nameEn" | "descriptionAr" | "descriptionEn" | "discount" | "minQty", o: { lang?: "ar" | "en"; num?: boolean; ph?: string } = {}) => (
    <input id={`ie-${k}`} className={`control ${o.num ? "num" : ""}`} value={v[k]} onChange={(e) => set(k, e.target.value)} autoComplete="off" placeholder={o.ph}
      {...(o.lang ? { lang: o.lang, dir: o.lang === "ar" ? "rtl" : "ltr" } : {})} {...(o.num ? { inputMode: "decimal" as const, dir: "ltr", style: { textAlign: "start" as const } } : {})} />
  );
  const cell = (i: number, f: "pieces" | "priceWholesale" | "priceHalf" | "priceRetail", label: string, extra: { readOnly?: boolean; title?: string; ph?: string } = {}) => {
    const err = errs[`units.${i}.${f}`];
    return (
      <td className="w-num"><input className={`control num ${err ? "invalid" : ""}`} inputMode="decimal" dir="ltr" value={v.units[i]![f]} aria-label={`${label} ${i + 1}`} aria-invalid={!!err} title={err ?? extra.title}
        readOnly={extra.readOnly} placeholder={extra.ph} onChange={(e) => setUnit(i, { [f]: e.target.value })} data-testid={`unit-${i}-${f}`} />
        {err && <div className="ferr" role="alert">{err}</div>}</td>
    );
  };
  const stored = saved ?? src;
  const shownImage = image ? image.url : stored?.image ? `/api/items/${stored.id}/image?v=${encodeURIComponent(stored.image.updatedAt)}` : null;

  const bar = (
    <div className="action-bar">
      <div className="ab-stats"><div><div className="k">{t("units")}</div><div className="v num">{v.units.length}</div></div><div><div className="k">{t("barcodes")}</div><div className="v num">{barcodeCount}</div></div></div>
      <div className="ab-actions">
        <button className="btn" onClick={() => nav(stored ? `/items/show/${stored.id}` : "/items/allItems")}>{t("cancel")}</button>
        <button className="btn btn-primary" disabled={busy} onClick={() => void save()} data-testid="save-item"><Icon name="check" />{busy ? t("saving") : saved ? t("retryImage") : t("saveItem")}</button>
      </div>
    </div>
  );

  return (
    <Shell navKey="/items/allItems" bar={bar}>
      <PageHead crumbs={crumbs} title={title} />
      <div className="inv-stack">
        {imageFailed && (
          <div className="dlg-alert show" role="alert" data-testid="image-failed">
            <Icon name="alert" />
            <span><b>{t("itemSavedImageFailed", { x: L(saved ?? src) })}</b> {imageFailed}</span>
            <span className="spacer" />
            <button type="button" className="btn btn-sm btn-primary" disabled={busy} onClick={() => void retryImage()} data-testid="retry-image"><Icon name="refresh" />{t("retryImage")}</button>
            <button type="button" className="btn btn-sm" onClick={() => finish((saved ?? src)!)} data-testid="skip-image">{t("continueWithoutImage")}</button>
          </div>
        )}
        <fieldset className="bare" disabled={!!saved}>
        <section className="card"><div className="card-head"><h2>{t("itemData")}</h2></div><div className="card-body"><div className="form-grid">
          <Field label={t("category")} req error={errs.categoryId} htmlFor="ie-categoryId">
            <select id="ie-categoryId" className="control" value={v.categoryId} onChange={(e) => set("categoryId", e.target.value)}>
              <option value="">{t("choose")}</option>{opts.categories.map((c) => <option key={c.id} value={c.id}>{L(c)}</option>)}
            </select>
          </Field>
          <Field label={t("itemCode")} req error={errs.code} htmlFor="ie-code">
            <div className="input-group">{inp("code", { ph: "ABC-100" })}<button type="button" className="btn" onClick={() => void autoCode()}><Icon name="refresh" />{t("autoGenerate")}</button></div>
          </Field>
          <Field label={t("nameAr")} req error={errs.nameAr} htmlFor="ie-nameAr">{inp("nameAr", { lang: "ar" })}</Field>
          <Field label={t("nameEn")} req error={errs.nameEn} htmlFor="ie-nameEn">{inp("nameEn", { lang: "en" })}</Field>
          <Field label={t("descriptionAr")} error={errs.descriptionAr} htmlFor="ie-descriptionAr">{inp("descriptionAr", { lang: "ar" })}</Field>
          <Field label={t("descriptionEn")} error={errs.descriptionEn} htmlFor="ie-descriptionEn">{inp("descriptionEn", { lang: "en" })}</Field>
          <Field label={t("discountPct")} error={errs.discount} htmlFor="ie-discount">{inp("discount", { num: true })}</Field>
          <Field label={t("minQty")} error={errs.minQty} htmlFor="ie-minQty">{inp("minQty", { num: true })}</Field>
        </div></div></section>

        <section className="card"><div className="card-head"><h2>{t("units")} <span className="count num">{v.units.length}</span></h2></div>
          <div className="lines-toolbar"><span className="hint">{t("unitsHint")} {t("pricesHint")}</span><span className="spacer" />
            <button className="btn btn-sm" onClick={() => setV({ ...v, units: [...v.units, blankUnit(opts.units.find((u) => u.id !== v.units[0]?.unitId)?.id ?? opts.units[0]?.id ?? "", "12")] })} data-testid="add-unit"><Icon name="plus" />{t("add")}</button></div>
          <div className="table-wrap"><table className="tbl lines-table units-grid" data-testid="units-grid">
            <thead><tr><th>{t("unit")}</th><th>{t("piecesNo")}</th><th>{t("price_wholesale")}</th><th>{t("price_half")}</th><th>{t("price_retail")}</th><th>{t("unitBarcode")}</th><th className="end">{t("delete")}</th></tr></thead>
            <tbody>{v.units.map((u, i) => (
              <tr key={u.key}>
                <td className="w-unitsel">
                  <select className={`control ${errs[`units.${i}.unitId`] ? "invalid" : ""}`} value={u.unitId} aria-label={`${t("unit")} ${i + 1}`} onChange={(e) => setUnit(i, { unitId: e.target.value })} data-testid={`unit-${i}-unitId`}>
                    {opts.units.map((x) => <option key={x.id} value={x.id}>{L(x)}</option>)}
                  </select>
                  {errs[`units.${i}.unitId`] && <div className="ferr" role="alert">{errs[`units.${i}.unitId`]}</div>}
                </td>
                {cell(i, "pieces", t("piecesNo"), i === 0 ? { readOnly: true, title: t("baseUnitHint") } : {})}
                {cell(i, "priceWholesale", t("price_wholesale"), { ph: u.priceRetail })}
                {cell(i, "priceHalf", t("price_half"), { ph: u.priceRetail })}
                {cell(i, "priceRetail", t("price_retail"))}
                <td className="w-bcs"><div className="bc-list">
                  {u.barcodes.map((b, j) => <span key={b} className="bc-chip num" dir="ltr">{b}<button type="button" className="x" aria-label={`${t("deleteBarcode")} ${b}`} title={t("deleteBarcode")} onClick={() => setUnit(i, { barcodes: u.barcodes.filter((_, k) => k !== j) })}><Icon name="x" /></button></span>)}
                  <span className="bc-add">
                    <input className="control num" dir="ltr" placeholder={t("addBarcodePh")} aria-label={`${t("unitBarcode")} ${i + 1}`} value={u.typed} title={t("unitBarcodesHint")} data-testid={`unit-${i}-barcode`}
                      onChange={(e) => setUnit(i, { typed: e.target.value })} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); void addBarcode(i, u.typed); } }} onBlur={() => { if (u.typed.trim()) void addBarcode(i, u.typed); }} />
                    <button type="button" className="btn btn-sm btn-ghost" title={t("autoGenerate")} aria-label={`${t("autoGenerate")} ${i + 1}`} onClick={() => void generate(i)} data-testid={`unit-${i}-generate`}><Icon name="refresh" /></button>
                  </span>
                </div>{errs[`units.${i}.barcodes`] && <div className="ferr" role="alert">{errs[`units.${i}.barcodes`]}</div>}</td>
                <td className="end"><button className="btn btn-sm btn-icon btn-danger-ghost" disabled={v.units.length <= 1 || i === 0} aria-label={`${t("delete")} ${i + 1}`}
                  onClick={() => { setV({ ...v, units: v.units.filter((_, j) => j !== i) }); setErrs({}); }}><Icon name="trash" /></button></td>
              </tr>
            ))}</tbody>
          </table></div>
        </section>

        </fieldset>
        <section className="card"><div className="card-head"><h2>{t("itemSettings")}</h2></div><div className="card-body"><div className="form-grid">
          <fieldset className="bare full-row" disabled={!!saved}>
          <Field label={t("itemKind")} req error={errs.itemKindId} htmlFor="if-kind">
            <select id="if-kind" className="control" value={v.itemKindId} onChange={(e) => set("itemKindId", e.target.value)}>
              <option value="">{t("choose")}</option>{opts?.itemKinds.map((k) => <option key={k.id} value={k.id}>{L(k)}</option>)}
            </select>
          </Field>
          <Field label={t("taxType")} req error={errs.taxTypeId} htmlFor="if-tax">
            <select id="if-tax" className="control" value={v.taxTypeId} onChange={(e) => set("taxTypeId", e.target.value)}>
              <option value="">{t("choose")}</option>{opts?.taxTypes.map((x) => <option key={x.id} value={x.id}>{L(x)} ({pct(x.percentage)})</option>)}
            </select>
          </Field>
          <label className="check-row"><input type="checkbox" checked={v.priceIncludeTax} onChange={(e) => set("priceIncludeTax", e.target.checked)} /> {t("priceIncludeTax")}</label>
          <label className="check-row" title={t("tobItemHint")}><input type="checkbox" data-testid="item-tobacco" checked={v.tobaccoFee} onChange={(e) => set("tobaccoFee", e.target.checked)} /> {t("tobItemFlag")}</label>
          <label className="check-row"><input type="checkbox" checked={v.quickList} onChange={(e) => set("quickList", e.target.checked)} /> {t("quickListPos")}</label>
          </fieldset>
          <div className="field full"><span className="label">{t("itemImage")}</span>
            {shownImage
              ? <div className="img-pick"><img src={shownImage} alt="" data-testid="item-image" /><span className="fname">{image?.file?.name ?? ""}</span><button type="button" className="btn btn-sm btn-ghost" onClick={() => setImage({ file: null, url: null })}><Icon name="x" />{t("remove")}</button></div>
              : <label className="dropzone sm"><Icon name="upload" /><b>{t("chooseImage")}</b><span className="hint">{t("itemImageHint")}</span>
                  <input type="file" accept={IMAGE_TYPES.join(",")} className="sr-only" data-testid="image-input" onChange={(e) => { chooseImage(e.target.files?.[0]); e.target.value = ""; }} /></label>}
          </div>
        </div></div></section>
        <div className={`dlg-alert ${alert ? "show" : ""}`} role="alert" data-testid="item-alert">{alert && <><Icon name="alert" /><span>{alert}</span></>}</div>
      </div>
    </Shell>
  );
}
